/**
 * Information that represents a resource that is registered in the application.
 * @public
 */
export interface Resource {
	/**
	 * Name of the resource (by default must be unique).
	 * @public
	 */
	name: string;
}

function registerResource<T extends Resource>(registry: Set<T>, allowDuplications: boolean, resource: T): void {
	if (registry.has(resource)) {
		return;
	}

	const registered = Array.from(registry).find(r => r.name === resource.name);
	if (registered && !allowDuplications) {
		console.groupCollapsed(`Resource ${resource.name} is already registered and must be unique.`);
		console.info(`Current:`, registered);
		console.info(`New:`, resource);
		console.groupEnd();
		return;
	}

	registry.add(Object.freeze(resource));
}

function getResources<T extends Resource>(registry: Set<T>): T[] {
	return Array.from(registry);
}

function getResource<T extends Resource>(registry: Set<T>, name: string): T | undefined {
	return Array.from(registry).find(r => r.name === name);
}

function removeResource<T extends Resource>(registry: Set<T>, resource: T | string): void {
	if (typeof resource === 'string') {
		Array.from(registry).forEach(r => {
			if (r.name === resource) {
				registry.delete(r);
			}
		});
	} else {
		registry.delete(resource);
	}
}

/**
 * Registry of resources
 * @public
 */
export interface ResourceRegistry<T extends Resource> {
	/**
	 * Removes a resource from the registry
	 * @internal
	 * @param resource - Name of the resource or resource itself to remove
	 */
	remove(resource: T | string): void;
	/**
	 * Retrieves a single registered resource based on the name of it.
	 * @public
	 * @param name - Name of the resource
	 * @returns Registered resource or undefined if the resource is not registered
	 */
	get(name: string): T | undefined;
	/**
	 * Retrieves all the registered resources.
	 * @public
	 * @returns Array of resources
	 */
	list(): T[];
	/**
	 * Register a resource into the global record.
	 * @public
	 * @remarks For security reasons, the resource will be frozen with `Object.freeze`.
	 * @param resource - Library information
	 */
	add(resource: T): void;
}

/**
 * Creates a new registry of resources
 * @public
 * @param allowDuplications - If true, allows duplicated resources by name
 * @returns Resource registry
 */
export function createResourceRegistry<T extends Resource>(allowDuplications = false): ResourceRegistry<T> {
	const resourceRegistry = new Set<T>();
	return {
		add: registerResource.bind(null, resourceRegistry, allowDuplications),
		list: getResources.bind<null, Set<T>, [], T[]>(null, resourceRegistry),
		get: getResource.bind<null, Set<T>, [name: string], T | undefined>(null, resourceRegistry),
		remove: removeResource.bind(null, resourceRegistry),
	};
}
